import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { authService } from '../../../authService';
import LogBookHome from './pages/LogBookHome';
import DocumentTracking from './pages/DocumentTracking';
import LogBookReports from './pages/LogBookReports';
import LogBookSettings from './pages/LogBookSettings';
import BIRForms from './pages/BIRForms';
import Suppliers from './pages/Suppliers';
import ProfileModal from '../../modals/ProfileModal';
import SettingsModal from '../../modals/SettingsModal';
import MobileBottomBar from '../../common/MobileBottomBar';
import UnifiedSidebar from '../../common/UnifiedSidebar';
import { SystemThemeProvider } from '../../../hooks/useSystemTheme';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  FactCheck,
  Assessment,
  Settings as SettingsIcon,
  ArrowBack,
  MenuBook,
  Logout,
  Person,
  Description,
  PeopleAlt as PeopleAltIcon
} from '@mui/icons-material';

const drawerWidth = 280;

const ELogBookDashboard = ({ onLogout }) => {
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const currentUser = authService.getUserSession();
    if (!currentUser) {
      navigate('/login');
    } else {
      setUser(currentUser);
    }
    document.title = 'e-LogBook System - LGU Concepcion';
  }, [navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    authService.logout();
    if (onLogout) {
      onLogout();
    }
    navigate('/login', { replace: true });
  };

  const handleBackToPortal = () => {
    sessionStorage.removeItem('currentSystem');
    navigate('/portal');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    setShowProfileModal(true);
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    setShowSettingsModal(true);
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
    authService.setUserSession(updatedUser);
  };

  const getMenuItems = () => {
    const allMenuItems = [
      { name: 'Dashboard', icon: <Dashboard />, path: '/portal/e-logbook/home' },
      { name: 'Document Tracking', icon: <FactCheck />, path: '/portal/e-logbook/tracking' },
      { name: 'Reports', icon: <Assessment />, path: '/portal/e-logbook/reports' },
      { name: 'BIR Forms', icon: <Description />, path: '/portal/e-logbook/bir-forms' },
      { name: 'Suppliers', icon: <PeopleAltIcon />, path: '/portal/e-logbook/suppliers' },
      { name: 'Settings', icon: <SettingsIcon />, path: '/portal/e-logbook/settings' }
    ];

    if (user?.role === 'user') {
      return allMenuItems.filter(item => item.name === 'Dashboard');
    }

    return allMenuItems;
  };

  const menuItems = getMenuItems();

  const drawer = (
    <UnifiedSidebar
      user={user}
      onLogout={handleLogout}
      onCloseMobile={() => setMobileOpen(false)}
      onOpenProfile={handleProfileClick}
      onOpenSettings={handleSettingsClick}
    />
  );

  if (!user) return null;

  return (
    <SystemThemeProvider systemId="e-logbook">
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        
        {/* Mobile Top AppBar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
            display: { md: 'none' },
            bgcolor: '#ffffff',
            color: '#0f172a',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1.5, color: '#0f172a' }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MenuBook sx={{ color: '#059669', fontSize: 22 }} />
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
                e-LogBook System
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Side Navigation Drawers */}
        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            pb: { xs: 10, md: 4 },
            width: { md: `calc(100% - ${drawerWidth}px)` },
            bgcolor: '#f8faf9',
            minHeight: '100vh',
            mt: { xs: 7, md: 0 }
          }}
        >
          <Routes>
            <Route path="home" element={<LogBookHome user={user} />} />
            <Route path="tracking" element={<DocumentTracking user={user} />} />
            <Route path="bir-forms" element={<BIRForms user={user} />} />
            <Route path="suppliers" element={<Suppliers user={user} />} />
            <Route path="reports" element={<LogBookReports user={user} />} />
            <Route path="settings" element={<LogBookSettings user={user} />} />
            <Route path="*" element={<LogBookHome user={user} />} />
          </Routes>
        </Box>

        {/* Sticky Mobile Bottom Navigation */}
        <MobileBottomBar
          items={menuItems}
          currentPath={location.pathname}
          onNavigate={(path) => navigate(path)}
          onToggleMenu={handleDrawerToggle}
          isMenuOpen={mobileOpen}
        />

        {/* Modals */}
        <ProfileModal
          user={user}
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          onUpdate={handleProfileUpdate}
        />
        <SettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
        />
      </Box>
    </SystemThemeProvider>
  );
};

export default ELogBookDashboard;
